import { seaWaybillStrings } from '@data/seaWaybill';
import { CargoCoop, TransportStatus } from '@models/TrackingData';
import { createStrings } from '@utils/local';

export const cargoTrackingStrings = createStrings({
  ja: {
    bookingConfirmed: '予約確定',
    labelGeneration: 'ラベル発行',
    deliveryGuidance: '通関と納品の最終指示',
    exportMovedIn: '荷受け倉庫へ到着',
    chinaFactoryPickup: '中国工場集荷',
    exportClearance: '輸出通関許可',
    exportMovedOut: '輸出港より出港',
    importArrived: '輸入港に入港',
    arriveAtTheWarehouse: '倉庫に到着',
    importCustomsDeclaration: '輸入通関申告',
    declaration1: '本申告入り 区1',
    declaration2: '本申告入り 区2',
    declaration3: '本申告入り 区3',
    customsCheck: '税関検査',
    importDeclared: '輸入許可',
    domesticShipped: '国内配達中',
    done: 'お届け完了',
    cancelled: 'キャンセル済',
    seaWaybillGenerated: 'Sea Waybill発行',
    arrivalNoticeGenerated: 'Arrival Notice生成',
    invoiceIssuance: '請求書発行',
    realTimeAccount: 'リアルタイム口座振替通知書',
    invoiceIssuanceCompleted: '請求書全部発行完了',
    invoicePaid: '請求書支払済',
    paymentSlipIssuance: '支払伝票発行',
    paymentSlipIssuanceCompleted: '支払伝票全部発行完了',
    paymentSlipPaid: '支払伝票支払済',
    transportStatus: '輸送状況',
    d2dNo: 'D2D番号',
    date: '日付',
    time: '時刻',
    place: '場所',
    files: '書類',
    domesticMethod: '国内運輸方法',
    coopTrackNo: '追跡番号',
    carNo: '車両番号',
    carDriver: 'ドライバー名',
    customsInstructionConfirmed: '通関依頼済',
    rcepInstructionConfirmed: 'RCEP依頼済',
    deliveryInstructionConfirmed: '配達手配済',
    prealertGenerated: 'Pre-Alert発行',
    individualInsurance: '個別保険証券',
    driverInfoAndDeliveryInvoice: 'ドライバー情報・送り状',
    downloadFile: '書類ダウンロード',
  },
} as const);

export const coopSites: Record<CargoCoop, string> = {
  西濃運輸: 'https://track.seino.co.jp/cgi-bin/gnpquery.pgm',
  福山通運: 'https://corp.fukutsu.co.jp/situation/tracking_no',
  トナミ運輸: 'https://trc1.tonami.co.jp/trc/search3/excSearch3',
  第一貨物: 'https://www.daiichi-kamotsu.co.jp/chase/contact_num/',
  久留米運送: 'https://www4.kurumeunsou.co.jp/kurume-trans/asp_toiawase.asp',
  山陽自動車運送: 'https://www.sanyoju.co.jp/Kamotsu/Inquire1.html',
  名鉄運輸: 'https://ap.meitetsuunyu.co.jp/webtrace/webtsuiseki/webtsuiseki.aspx',
  佐川急便: 'https://k2k.sagawa-exp.co.jp/p/sagawa/web/okurijoinput.jsp',
  日通トランスポート: 'https://ap.meitetsuunyu.co.jp/webtrace/webtsuiseki/webtsuiseki.aspx',
  日本通運: 'https://ap.meitetsuunyu.co.jp/webtrace/webtsuiseki/webtsuiseki.aspx',
  備後通運: 'https://ap.meitetsuunyu.co.jp/webtrace/webtsuiseki/webtsuiseki.aspx',
  'エスラインギフ S LINE': 'http://www.sline.co.jp/baggage/pkt01inf.html',
  チャータートラック: '',
  コンテナ: '',
};

export interface TrackingDataMetaItem {
  id: 'Asia/Shanghai' | 'Asia/Tokyo';
  name: 'CST' | 'JST';
  fileName: string;
  visibleInCargoTracking: boolean;
}

export const trackingDataMeta = {
  [cargoTrackingStrings.bookingConfirmed]: {
    id: 'Asia/Shanghai',
    name: 'CST',
    fileName: cargoTrackingStrings.downloadFile,
    visibleInCargoTracking: true,
  },
  [cargoTrackingStrings.prealertGenerated]: {
    id: 'Asia/Shanghai',
    name: 'CST',
    fileName: cargoTrackingStrings.downloadFile,
    visibleInCargoTracking: false,
  },
  [cargoTrackingStrings.labelGeneration]: {
    id: 'Asia/Shanghai',
    name: 'CST',
    fileName: cargoTrackingStrings.downloadFile,
    visibleInCargoTracking: false,
  },
  [cargoTrackingStrings.deliveryGuidance]: {
    id: 'Asia/Shanghai',
    name: 'CST',
    fileName: cargoTrackingStrings.downloadFile,
    visibleInCargoTracking: false,
  },
  [cargoTrackingStrings.chinaFactoryPickup]: {
    id: 'Asia/Shanghai',
    name: 'CST',
    fileName: cargoTrackingStrings.downloadFile,
    visibleInCargoTracking: false,
  },
  [cargoTrackingStrings.exportMovedIn]: {
    id: 'Asia/Shanghai',
    name: 'CST',
    fileName: cargoTrackingStrings.downloadFile,
    visibleInCargoTracking: true,
  },
  [cargoTrackingStrings.exportClearance]: {
    id: 'Asia/Shanghai',
    name: 'CST',
    fileName: cargoTrackingStrings.downloadFile,
    visibleInCargoTracking: true,
  },
  [cargoTrackingStrings.exportMovedOut]: {
    id: 'Asia/Shanghai',
    name: 'CST',
    fileName: cargoTrackingStrings.downloadFile,
    visibleInCargoTracking: true,
  },
  [cargoTrackingStrings.seaWaybillGenerated]: {
    id: 'Asia/Shanghai',
    name: 'CST',
    fileName: seaWaybillStrings.seaWaybill,
    visibleInCargoTracking: false,
  },
  [cargoTrackingStrings.importArrived]: {
    id: 'Asia/Tokyo',
    name: 'JST',
    fileName: cargoTrackingStrings.downloadFile,
    visibleInCargoTracking: true,
  },
  [cargoTrackingStrings.arrivalNoticeGenerated]: {
    id: 'Asia/Shanghai',
    name: 'CST',
    fileName: cargoTrackingStrings.downloadFile,
    visibleInCargoTracking: false,
  },
  [cargoTrackingStrings.arriveAtTheWarehouse]: {
    id: 'Asia/Tokyo',
    name: 'JST',
    fileName: cargoTrackingStrings.downloadFile,
    visibleInCargoTracking: false,
  },
  [cargoTrackingStrings.importCustomsDeclaration]: {
    id: 'Asia/Tokyo',
    name: 'JST',
    fileName: cargoTrackingStrings.downloadFile,
    visibleInCargoTracking: true,
  },
  [cargoTrackingStrings.importDeclared]: {
    id: 'Asia/Tokyo',
    name: 'JST',
    fileName: '輸入許可書',
    visibleInCargoTracking: true,
  },
  [cargoTrackingStrings.domesticShipped]: {
    id: 'Asia/Tokyo',
    name: 'JST',
    fileName: cargoTrackingStrings.downloadFile,
    visibleInCargoTracking: true,
  },
  [cargoTrackingStrings.done]: {
    id: 'Asia/Tokyo',
    name: 'JST',
    fileName: '受領書',
    visibleInCargoTracking: true,
  },
  [cargoTrackingStrings.cancelled]: {
    id: 'Asia/Tokyo',
    name: 'JST',
    fileName: cargoTrackingStrings.downloadFile,
    visibleInCargoTracking: true,
  },
  [cargoTrackingStrings.invoiceIssuance]: {
    id: 'Asia/Tokyo',
    name: 'JST',
    fileName: cargoTrackingStrings.downloadFile,
    visibleInCargoTracking: false,
  },
  [cargoTrackingStrings.invoiceIssuanceCompleted]: {
    id: 'Asia/Tokyo',
    name: 'JST',
    fileName: cargoTrackingStrings.downloadFile,
    visibleInCargoTracking: false,
  },
  [cargoTrackingStrings.invoicePaid]: {
    id: 'Asia/Tokyo',
    name: 'JST',
    fileName: cargoTrackingStrings.downloadFile,
    visibleInCargoTracking: false,
  },
  [cargoTrackingStrings.paymentSlipIssuance]: {
    id: 'Asia/Tokyo',
    name: 'JST',
    fileName: cargoTrackingStrings.downloadFile,
    visibleInCargoTracking: false,
  },
  [cargoTrackingStrings.paymentSlipIssuanceCompleted]: {
    id: 'Asia/Tokyo',
    name: 'JST',
    fileName: cargoTrackingStrings.downloadFile,
    visibleInCargoTracking: false,
  },
  [cargoTrackingStrings.paymentSlipPaid]: {
    id: 'Asia/Tokyo',
    name: 'JST',
    fileName: cargoTrackingStrings.downloadFile,
    visibleInCargoTracking: false,
  },
  [cargoTrackingStrings.customsInstructionConfirmed]: {
    id: 'Asia/Tokyo',
    name: 'JST',
    fileName: cargoTrackingStrings.downloadFile,
    visibleInCargoTracking: false,
  },
  [cargoTrackingStrings.deliveryInstructionConfirmed]: {
    id: 'Asia/Tokyo',
    name: 'JST',
    fileName: cargoTrackingStrings.downloadFile,
    visibleInCargoTracking: false,
  },
  [cargoTrackingStrings.rcepInstructionConfirmed]: {
    id: 'Asia/Tokyo',
    name: 'JST',
    fileName: cargoTrackingStrings.downloadFile,
    visibleInCargoTracking: false,
  },
  [cargoTrackingStrings.individualInsurance]: {
    id: 'Asia/Tokyo',
    name: 'JST',
    fileName: '個別保険',
    visibleInCargoTracking: false,
  },
  [cargoTrackingStrings.driverInfoAndDeliveryInvoice]: {
    id: 'Asia/Tokyo',
    name: 'JST',
    fileName: '送り状',
    visibleInCargoTracking: false,
  },
  [cargoTrackingStrings.realTimeAccount]: {
    id: 'Asia/Tokyo',
    name: 'JST',
    fileName: '通知書',
    visibleInCargoTracking: false,
  },
  [cargoTrackingStrings.customsCheck]: {
    id: 'Asia/Tokyo',
    name: 'JST',
    fileName: '検査指定票',
    visibleInCargoTracking: false,
  },
  [cargoTrackingStrings.declaration1]: {
    id: 'Asia/Tokyo',
    name: 'JST',
    fileName: cargoTrackingStrings.downloadFile,
    visibleInCargoTracking: false,
  },
  [cargoTrackingStrings.declaration2]: {
    id: 'Asia/Tokyo',
    name: 'JST',
    fileName: cargoTrackingStrings.downloadFile,
    visibleInCargoTracking: false,
  },
  [cargoTrackingStrings.declaration3]: {
    id: 'Asia/Tokyo',
    name: 'JST',
    fileName: cargoTrackingStrings.downloadFile,
    visibleInCargoTracking: false,
  },
} satisfies Record<TransportStatus, TrackingDataMetaItem>;

export type VisibleInCargoTrackingTransportStatus = {
  [K in keyof typeof trackingDataMeta]: (typeof trackingDataMeta)[K]['visibleInCargoTracking'] extends true
    ? K
    : never;
}[keyof typeof trackingDataMeta];
